import { Button } from "@/modules/shadcn/ui/button";
import { NumberInput } from "@/modules/shadcn/ui/number-input";
import { Switch } from "@/modules/shadcn/ui/switch";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import z from "zod";
import { useCaptureImageFromCamera } from "../../hooks/useCaptureImageFromCamera";
import { CaptureStillImageCountdown } from "../CaptureStillImageCountdown";

// Gates the countdown only (`.success`); its issues are never shown, so no messages.
const intervalShootingConfigSchema = z.object({
  numberOfImages: z.number().int().min(1),
  intervalSeconds: z.number().min(0.1),
});

type IntervalShootingConfigForm = {
  numberOfImages: number | null;
  intervalSeconds: number | null;
};
export type IntervalShootingConfig = z.output<
  typeof intervalShootingConfigSchema
>;

export interface CaptureStillImageProps {
  selectedCamera: string;
  selectedStream: string | null;
  isStreaming: boolean;
  isIntervalCapturing: boolean;
  onIntervalCapturingChange: (value: boolean) => void;
}

export const CaptureStillImage = ({
  selectedCamera,
  selectedStream,
  isStreaming,
  isIntervalCapturing,
  onIntervalCapturingChange,
}: CaptureStillImageProps) => {
  const { t } = useTranslation("capture");
  const { handleCaptureImage, isLoading } = useCaptureImageFromCamera();
  const [useIntervalShooting, setUseIntervalShooting] = useState(false);
  const [intervalShootingConfig, setIntervalShootingConfig] =
    useState<IntervalShootingConfigForm>({
      numberOfImages: null,
      intervalSeconds: null,
    });

  const intervalShootingConfigResult = intervalShootingConfigSchema.safeParse(
    intervalShootingConfig,
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex w-full items-center justify-between gap-2">
        <span className="overflow-hidden text-ellipsis whitespace-nowrap [width:calc(100%-8.25rem)]">
          {t("stillImage.useInterval")}
        </span>

        <Switch
          checked={useIntervalShooting}
          onCheckedChange={(value) => {
            setUseIntervalShooting(value);
          }}
          disabled={!selectedStream || !isStreaming}
        />
      </div>

      {useIntervalShooting && (
        <>
          <NumberInput
            label={t("stillImage.total")}
            placeholder={t("stillImage.totalPlaceholder")}
            value={intervalShootingConfig.numberOfImages}
            onValueChange={(v) => {
              setIntervalShootingConfig({
                ...intervalShootingConfig,
                numberOfImages: v,
              });
            }}
          />
          <NumberInput
            decimal
            label={t("stillImage.interval")}
            placeholder={t("stillImage.interval")}
            suffix={t("stillImage.secondsSuffix")}
            value={intervalShootingConfig.intervalSeconds}
            onValueChange={(v) => {
              setIntervalShootingConfig({
                ...intervalShootingConfig,
                intervalSeconds: v,
              });
            }}
          />
        </>
      )}

      {useIntervalShooting ? (
        intervalShootingConfigResult.success ? (
          <CaptureStillImageCountdown
            selectedCamera={selectedCamera}
            selectedStream={selectedStream}
            isStreaming={isStreaming}
            intervalShootingConfig={intervalShootingConfigResult.data}
            isCapturing={isIntervalCapturing}
            onIsCapturingChange={onIntervalCapturingChange}
          />
        ) : null
      ) : (
        <Button
          onClick={() => handleCaptureImage(selectedCamera!, selectedStream!)}
          disabled={!selectedCamera || !selectedStream || !isStreaming || isLoading}
          size="lg"
        >
          {t("stillImage.capture")}
        </Button>
      )}
    </div>
  );
};
